import React, { useEffect, useRef, useState } from "react";

import useReviewTarget from "../useReviewTarget";
import TargetIdentity from "./TargetIdentity";
import { RatingLine } from "./Stars";
import { TargetLineSkeleton } from "./ReviewsSkeletons";
import { REVIEWS_COPY } from "../reviewsCopy";

const ReviewCard = ({ review, busy = false, onEdit, onDelete }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const cardRef = useRef(null);

  const target = useReviewTarget({
    targetType: review.targetType,
    targetId: review.targetId,
  });

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onDocumentClick = (event) => {
      if (!cardRef.current?.contains(event.target)) setMenuOpen(false);
    };

    document.addEventListener("mousedown", onDocumentClick);

    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, [menuOpen]);

  const choose = (action) => {
    setMenuOpen(false);
    action(review, target.name);
  };

  return (
    <li className="rv-card" ref={cardRef}>
      <header className="rv-card-head">
        <TargetIdentity name={target.name} imageUrl={target.imageUrl} size={44} />

        <div className="rv-card-identity">
          {target.isLoading ? (
            <TargetLineSkeleton />
          ) : (
            <p className="rv-card-name">{target.name || target.subtitle}</p>
          )}

          <p className="rv-card-sub">{target.subtitle}</p>
        </div>

        <button
          type="button"
          className="rv-card-more"
          aria-label="Review options"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <svg viewBox="0 0 20 20" aria-hidden="true">
            <circle cx="4" cy="10" r="1.5" />
            <circle cx="10" cy="10" r="1.5" />
            <circle cx="16" cy="10" r="1.5" />
          </svg>
        </button>

        {menuOpen && (
          <div className="rv-card-menu" role="menu">
            <button
              type="button"
              role="menuitem"
              className="rv-card-menu-item"
              disabled={busy}
              onClick={() => choose(onEdit)}
            >
              Edit Review
            </button>

            <button
              type="button"
              role="menuitem"
              className="rv-card-menu-item is-danger"
              disabled={busy}
              onClick={() => choose(onDelete)}
            >
              Delete Review
            </button>
          </div>
        )}
      </header>

      <span className="rv-card-rule" />

      <div className="rv-card-meta">
        <RatingLine rating={review.rating} />

        <span
          className={`rv-status ${review.active ? "rv-status-success" : "rv-status-muted"}`}
        >
          {review.active ? "Posted" : "Disabled"}
        </span>
      </div>

      <p className={`rv-card-text${review.review ? "" : " is-empty"}`}>
        {review.review || REVIEWS_COPY.noWrittenReview}
      </p>

      <p className="rv-card-date">
        {[review.createdDate, review.createdTime].filter(Boolean).join(" · ")}
      </p>
    </li>
  );
};

export default ReviewCard;
